import React, { useState, useEffect, forwardRef, useMemo } from "react";
import PropTypes from "prop-types";
import MDAvatarRoot from "dashboard/components/MDAvatar/MDAvatarRoot";

const defaultImagesUrl = {
  user: `${process.env.REACT_APP_PUBLIC_URL}/img/user-default.png`,
  product: `${process.env.REACT_APP_PUBLIC_URL}/img/product-default.png`,
  category: `${process.env.REACT_APP_PUBLIC_URL}/img/category-default.png`,
  store: `${process.env.REACT_APP_PUBLIC_URL}/img/store-default.jpg`,
  cover: `${process.env.REACT_APP_PUBLIC_URL}/img/image-default.png`,
  default: `${process.env.REACT_APP_PUBLIC_URL}/img/image-default.png`,
};

const MDAvatar = forwardRef(({ bgColor, size, shadow, src, imgType = "default", ...rest }, ref) => {
  const [avatar, setAvatar] = useState(src);

  useEffect(() => {
    const image = new Image();
    image.src = src;
    image.onload = () => {
      if (src !== avatar) {
        setAvatar(src);
      }
    };
    image.onerror = () => {
      const defaultImg = defaultImagesUrl[imgType] || defaultImagesUrl.default;
      if (defaultImg !== avatar) {
        setAvatar(defaultImg);
      }
    };
  }, [src, imgType, avatar]);

  // Memoize the rendered avatar to prevent unnecessary renders
  const memoizedAvatar = useMemo(() => (
    <MDAvatarRoot ref={ref} src={avatar} ownerState={{ shadow, bgColor, size }} {...rest} />
  ), [avatar, bgColor, size, shadow]);

  return memoizedAvatar;
});

MDAvatar.defaultProps = {
  bgColor: "transparent",
  size: "md",
  shadow: "none",
  imgType: "default",
};

MDAvatar.propTypes = {
  bgColor: PropTypes.oneOf([
    "transparent", "primary", "secondary", "info", "success", "warning", "error", "light", "dark"
  ]),
  size: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl", "xxl"]),
  shadow: PropTypes.oneOf(["none", "xs", "sm", "md", "lg", "xl", "xxl", "inset"]),
  src: PropTypes.string.isRequired,
  imgType: PropTypes.oneOf(["user", "product", "category", "store", "cover", "default"]),
};

export default MDAvatar;
